/**
 * Pac-12 Conference Football Tiebreaker — 2026 policy.
 *
 * The 2026 Pac-12 plays a conference round-robin, so head-to-head results
 * are the primary separator.
 *
 * Two-Team Tie:
 *   1. Head-to-head record
 *   2. Winning percentage vs. all common conference opponents
 *   3. Winning percentage vs. common opponents in descending order of
 *      conference finish (proceeding through the standings)
 *   4. Random draw
 *
 * Three-or-More-Team Tie:
 *   a. Winning percentage in games among the tied teams (head-to-head)
 *   b. Winning percentage vs. all common conference opponents
 *   c. Winning percentage vs. common opponents in descending order of
 *      conference finish (proceeding through the standings)
 *   d. Random draw
 *
 * Teams are peeled off one at a time and the process restarts with the
 * remaining group, per the standard "revert to the beginning of the
 * applicable procedure" rule.
 *
 * Inputs:
 * - tiedTeams: array of { team, ... }
 * - confTeams: standings array [{ team, conferenceWins, conferenceGames }]
 * - games: array of [{ homeTeam, awayTeam, winner, conferenceGame, exempt }]
 * - options: { rng: () => number }
 *
 * Returns: ordered array of team objects (best/champion first).
 */

const EPS = 1e-9;
const nearlyEqual = (a, b) => a === b || Math.abs(a - b) < EPS;

export const resolvePac12Tie = (tiedTeams, confTeams, games, options = {}) => {
  if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];
  return resolveGroup(tiedTeams, confTeams, games, options);
};

const resolveGroup = (group, confTeams, games, options = {}) => {
  if (group.length === 0) return [];
  if (group.length === 1) return group;
  if (group.length === 2) {
    return resolveTwoTeamTie(group[0], group[1], confTeams, games, options);
  }
  return resolveMultiTeamTie(group, confTeams, games, options);
};

// ---------------------------------------------------------------- Multi-team
const resolveMultiTeamTie = (tiedTeams, confTeams, games, options = {}) => {
  const { rng = Math.random } = options;
  let remaining = [...tiedTeams];

  // Step a: winning pct in games among the tied teams
  const h2hMap = remaining.map(t => {
    const headGames = games.filter(
      g =>
        !g.exempt &&
        g.conferenceGame &&
        (g.homeTeam === t.team || g.awayTeam === t.team) &&
        remaining.some(r => r.team !== t.team && (g.homeTeam === r.team || g.awayTeam === r.team))
    );
    const wins = headGames.filter(g => g.winner === t.team).length;
    const total = headGames.length;
    const pct = total > 0 ? wins / total : null;
    return { teamObj: t, pct, wins, total };
  });

  const allPlayedEachOther = h2hMap.every(m => m.pct !== null);

  if (allPlayedEachOther) {
    const split = splitOnScores(
      remaining,
      t => h2hMap.find(m => m.teamObj.team === t.team).pct,
      confTeams,
      games,
      options
    );
    if (split) return split;
  } else {
    const defeatedAll = remaining.filter(t =>
      remaining
        .filter(o => o.team !== t.team)
        .every(other =>
          games.some(
            g =>
              !g.exempt &&
              g.conferenceGame &&
              ((g.homeTeam === t.team && g.awayTeam === other.team) ||
                (g.awayTeam === t.team && g.homeTeam === other.team)) &&
              g.winner === t.team
          )
        )
    );
    if (defeatedAll.length === 1) {
      const top = defeatedAll[0];
      const rest = remaining.filter(r => r.team !== top.team);
      return [...resolveGroup([top], confTeams, games, options), ...resolveGroup(rest, confTeams, games, options)];
    }
  }

  // Step b: win pct vs all common conference opponents
  const splitB = splitOnScores(remaining, t => winPctVsCommonOpponentsMulti(t.team, remaining, games), confTeams, games, options);
  if (splitB) return splitB;

  // Step c: win pct vs common opponents in descending order of finish
  const splitC = splitOnScores(
    remaining,
    t => winPctVsCommonOppsByOrderMulti(t.team, remaining, games, confTeams),
    confTeams,
    games,
    options
  );
  if (splitC) return splitC;

  // Step d: random draw
  return [...remaining].sort((a, b) => rng() - rng());
};

// ---------------------------------------------------------------- Two-team
const resolveTwoTeamTie = (A, B, confTeams, games, options = {}) => {
  const { rng = Math.random } = options;

  // 1. Head-to-head
  const h2h = headToHead(A.team, B.team, games);
  if (h2h) return h2h === A.team ? [A, B] : [B, A];

  // 2. Win % vs all common conference opponents
  const co = twoTeamCommonOppPct(A.team, B.team, games);
  if (co !== null) return co > 0 ? [A, B] : [B, A];

  // 3. Win % vs common opponents in descending order of finish
  const cobo = twoTeamVsCommonOppsByOrder(A.team, B.team, games, confTeams);
  if (cobo !== null) return cobo > 0 ? [A, B] : [B, A];

  // 4. Random draw
  return rng() < 0.5 ? [A, B] : [B, A];
};

// ---------------------------------------------------------------- Helpers
const isCountable = g => !g.exempt && g.conferenceGame;

const headToHead = (teamA, teamB, games) => {
  const matches = games.filter(
    g =>
      isCountable(g) &&
      ((g.homeTeam === teamA && g.awayTeam === teamB) || (g.homeTeam === teamB && g.awayTeam === teamA))
  );
  if (!matches.length) return null;
  const aWins = matches.filter(m => m.winner === teamA).length;
  const bWins = matches.filter(m => m.winner === teamB).length;
  if (aWins > bWins) return teamA;
  if (bWins > aWins) return teamB;
  return null;
};

const mapTeamGames = (team, games) =>
  games
    .filter(g => isCountable(g) && (g.homeTeam === team || g.awayTeam === team))
    .map(g => ({
      opponent: g.homeTeam === team ? g.awayTeam : g.homeTeam,
      won: g.winner === team
    }));

const twoTeamCommonOppPct = (Ateam, Bteam, games) => {
  const Amap = mapTeamGames(Ateam, games);
  const Bmap = mapTeamGames(Bteam, games);
  const Aopps = new Set(Amap.map(g => g.opponent));
  const Bopps = new Set(Bmap.map(g => g.opponent));
  const common = [...Aopps].filter(o => Bopps.has(o));
  if (!common.length) return null;

  const Agames = Amap.filter(g => common.includes(g.opponent));
  const Bgames = Bmap.filter(g => common.includes(g.opponent));
  const Apct = Agames.length ? Agames.filter(g => g.won).length / Agames.length : 0;
  const Bpct = Bgames.length ? Bgames.filter(g => g.won).length / Bgames.length : 0;
  if (nearlyEqual(Apct, Bpct)) return null;
  return Apct > Bpct ? 1 : -1;
};

const winPctVsCommonOpponentsMulti = (team, tiedTeams, games) => {
  const opponentsByTeam = tiedTeams.map(t => ({
    team: t.team,
    opponents: mapTeamGames(t.team, games).map(g => g.opponent)
  }));
  const commonOpps = opponentsByTeam
    .map(t => t.opponents)
    .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);
  if (!commonOpps.length) return 0;
  const teamGames = mapTeamGames(team, games).filter(g => commonOpps.includes(g.opponent));
  if (!teamGames.length) return 0;
  return teamGames.filter(g => g.won).length / teamGames.length;
};

const getConfPct = c => {
  if (
    typeof c.conferenceWins === 'number' &&
    typeof c.conferenceGames === 'number' &&
    c.conferenceGames > 0
  ) {
    return c.conferenceWins / c.conferenceGames;
  }
  if (typeof c.conferenceWins === 'number') return c.conferenceWins / 9;
  return 0;
};

const winPctVsCommonOppsByOrderMulti = (team, tiedTeams, games, confTeams) => {
  const opponentsByTeam = tiedTeams.map(t => ({
    team: t.team,
    opponents: mapTeamGames(t.team, games).map(g => g.opponent)
  }));
  const commonOpps = opponentsByTeam
    .map(t => t.opponents)
    .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);
  if (!commonOpps.length) return 0;

  const sortedOpps = confTeams
    .filter(c => commonOpps.includes(c.team))
    .sort((a, b) => getConfPct(b) - getConfPct(a));

  const teamGames = mapTeamGames(team, games);
  let wins = 0;
  let total = 0;
  for (const opp of sortedOpps) {
    const gamesVsOpp = teamGames.filter(g => g.opponent === opp.team);
    for (const g of gamesVsOpp) {
      total++;
      if (g.won) wins++;
    }
  }
  return total ? wins / total : 0;
};

const twoTeamVsCommonOppsByOrder = (Ateam, Bteam, games, confTeams) => {
  const aPct = winPctVsCommonOppsByOrderMulti(Ateam, [{ team: Ateam }, { team: Bteam }], games, confTeams);
  const bPct = winPctVsCommonOppsByOrderMulti(Bteam, [{ team: Ateam }, { team: Bteam }], games, confTeams);
  if (nearlyEqual(aPct, bPct)) return null;
  return aPct > bPct ? 1 : -1;
};

const splitOnScores = (remaining, scoreOf, confTeams, games, options) => {
  const scores = remaining.map(t => scoreOf(t));
  const maxScore = Math.max(...scores);
  const topGroup = remaining.filter((t, i) => nearlyEqual(scores[i], maxScore));
  const restGroup = remaining.filter((t, i) => !nearlyEqual(scores[i], maxScore));
  if (topGroup.length === 0 || topGroup.length === remaining.length) return null;

  const topOrdered = resolveGroup(topGroup, confTeams, games, options);
  const restOrdered = resolveGroup(restGroup, confTeams, games, options);
  return [...topOrdered, ...restOrdered];
};
